<template>
  <div>
    <aside>
      <v-navigation-drawer
        v-model="drawer"
        temporary
        light
        absolute
      >
        <v-list>
          <div
            v-for="item in items"
            :key="item.title"
          >
            <!-- primary items -->
            <v-list-tile
              v-if="item.route"
              :class="{'v-list__tile--active primary--text': isCurrentRoute(item) }"
              @click.stop="handleMenuItemClick(item)"
            >
              <v-list-tile-action>
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>{{ item.title }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <!-- drawers -->
            <v-list-group
              v-else
              :prepend-icon="item.icon"
              no-action
              :class="{'v-list__group--active': isCurrentRoute(item) }"
            >
              <template v-slot:activator>
                <v-list-tile :class="{'primary--text': isCurrentRoute(item) }">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ item.title }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </template>
              <v-list-tile
                v-for="subItem in item.items"
                :key="subItem.title"
                :class="{'v-list__tile--active primary--text': isCurrentRoute(subItem) }"
                @click.stop="handleMenuItemClick(subItem)"
              >
                <v-list-tile-action>
                  <v-icon>{{ subItem.icon }}</v-icon>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>{{ subItem.title }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list-group>
          </div>
        </v-list>
      </v-navigation-drawer>
    </aside>
    <div class="hide-overflow">
      <v-toolbar
        absolute
        flat
        dark
      >
        <v-toolbar-side-icon @click.stop="drawer = !drawer" />
        <v-toolbar-title>Exporo Admin Panel</v-toolbar-title>
        <v-spacer />
        <v-env-select />
        <v-toolbar-items class="hidden-sm-and-down">
          <v-btn
            v-if="user"
            flat
            @click="profile"
          >
            Profile
          </v-btn>
          <v-btn
            flat
            color="warning"
            @click="logOut"
          >
            Sign Out
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
    </div>
    <div class="progress">
      <div class="progressContent">
        <slot name="progress" />
      </div>
    </div>
  </div>
</template>

<script>
import EnvSelect from './EnvSelect';
import { signOut } from '@/utils/auth';
import { ROLE_GROUPS } from '@/router';

export default {
  name: 'Menu',
  components: {
    'v-env-select': EnvSelect,
  },
  data() {
    const { groups } = this.$store.state.userStore.user;
    const env = localStorage.getItem('Exporo.API.env');
    const isProduction = !env || env === 'prod';

    const menuItems = [
      {
        title: 'Users',
        icon: 'groups',
        roleGroups: [
          ROLE_GROUPS.SUPER_ADMIN,
          ROLE_GROUPS.ADMIN_BACKEND,
          ROLE_GROUPS.ADMIN_ONBOARDING,
        ],
        items: [
          {
            title: 'Backend Users',
            icon: 'person_search',
            route: '/users',
          },
          {
            title: 'Onboarding Users',
            icon: 'person_search',
            route: '/users/onboarding-users',
          },
          {
            title: 'Registrations',
            icon: 'group_add',
            route: '/users/registrations',
          },
        ],
      },
      {
        title: 'Email History',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_MAIL],
        icon: 'email',
        items: [
          {
            title: 'History',
            icon: 'history',
            route: '/emails',
          },
          {
            title: 'Template Types',
            icon: 'category',
            route: '/template-types',
          },
          {
            title: 'Templates',
            icon: 'dynamic_feed',
            route: '/templates',
          },
        ],
      },
      {
        title: 'Baader Bank',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_BAADER],
        icon: 'account_balance',
        items: [
          {
            title: 'Securities',
            icon: 'security',
            route: '/securities',
          },
          {
            title: 'Assets',
            icon: 'list',
            route: '/baader/assets',
          },
          {
            title: 'Accounts',
            icon: 'list',
            route: '/baader/accounts',
          },
          {
            title: 'Account Creations',
            icon: 'list',
            route: '/baader/account-creations',
          },
          {
            title: 'Order Items',
            icon: 'list',
            route: '/baader/order-items',
          },
          {
            title: 'Payout Items',
            icon: 'list',
            route: '/baader/payout-items',
          },
          {
            title: '2nd Market Orders',
            icon: 'list',
            route: '/baader/secondary-market-orders',
          },
        ],
      },
      {
        title: 'Blockchain',
        roleGroups: [
          ROLE_GROUPS.SUPER_ADMIN,
          ROLE_GROUPS.ADMIN_BLOCKCHAIN,
          ROLE_GROUPS.ADMIN_VAULT,
        ],
        icon: 'device_hub',
        items: [
          {
            title: 'Vault Transactions',
            icon: 'autorenew',
            route: '/vault/transactions',
          },
          {
            title: 'Vault wallets',
            icon: 'account_balance_wallet',
            route: '/vault/wallets',
          },
          {
            title: 'Vault assets',
            icon: 'business',
            route: '/vault/assets',
          },
        ],
      },
      {
        title: 'Trading Platform',
        icon: 'cached',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_TRADING],
        items: [
          {
            title: 'All Trades',
            icon: 'list',
            route: '/trading-platform/trades',
          },
          {
            title: 'Trades to transfer',
            icon: 'done_all',
            route: '/trading-platform/trades-to-transfer',
          },
          {
            title: 'Cancellable Trades',
            icon: 'cancel_schedule_send',
            route: '/trading-platform/cancellable-trades',
          },
          {
            title: 'Baaderbank trades',
            icon: 'account_balance',
            route: '/trading-platform/baaderbank-confirm-trades',
          },
          {
            title: 'Sell Offers',
            icon: 'swap_horiz',
            route: '/trading-platform/sell-offers',
          },
          {
            title: 'Buy Offers',
            icon: 'swap_horiz',
            route: '/trading-platform/buy-offers',
          },
        ],
      },
      {
        title: 'Emoney',
        icon: 'account_balance_wallet',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_EMONEY],
        items: [
          {
            title: 'Users',
            icon: 'groups',
            route: '/emoney-users',
          },
        ],
      },
      {
        title: 'Ident Cases',
        icon: 'fingerprint',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_IDENT],
        route: '/ident-cases',
      },
      {
        title: 'Bridge Signing Management',
        icon: 'description',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_IDENT],
        route: '/bridge-signing-management',
      },
      {
        title: 'Financial Products',
        icon: 'real_estate_agent',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_FINANCIAL],
        items: [
          {
            title: 'Products',
            icon: 'domain',
            route: '/financial-products/products',
          },
          {
            title: 'Files',
            icon: 'description',
            route: '/financial-products/files',
          },
          {
            title: 'Exposes',
            icon: 'book',
            route: '/financial-products/exposes',
          },
        ],
      },
      {
        title: 'IRE',
        icon: 'description',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_IRE],
        items: [
          {
            title: 'IP Signings',
            icon: 'insert_drive_file',
            route: '/ire/ip-signings',
          },
          {
            title: 'Direct Signings',
            icon: 'insert_drive_file',
            route: '/ire/direct-signings',
          },
          {
            title: 'DLQs',
            icon: 'running_with_errors',
            route: '/ire/dlq',
          },
        ],
      },
      {
        title: 'Digital Depots',
        icon: 'wallet',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_STRATEGIES],
        items: [
          {
            title: 'Accounts',
            icon: 'badge',
            route: '/dd/accounts',
          },
          {
            title: 'Strategies Accounts',
            icon: 'account_tree',
            route: '/digitalDepots/accounts',
          },
          {
            title: 'DLQs',
            icon: 'running_with_errors',
            route: '/digitalDepots/dlq',
          },
        ],
      },
      {
        title: 'Settlements',
        icon: 'account_balance',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_STRATEGIES],
        items: [
          {
            title: 'Settlements',
            icon: 'settings',
            route: '/settlements',
          },
          {
            title: 'Canceled Settlements',
            icon: 'settings',
            route: '/settlements/canceled',
          },
          {
            title: 'Tranche Utilizations',
            icon: 'settings',
            route: '/settlements/tranche',
          },
        ],
      },
      {
        title: 'Robo Investing',
        icon: 'account_tree',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_STRATEGIES],
        items: [
          {
            title: 'Savingsplans',
            icon: 'savings',
            route: '/robo-investing/savings-plans',
          },
          {
            title: 'FPV Reports',
            icon: 'summarize',
            route: '/robo-investing/fpv-reports',
          },
        ],
      },
      {
        title: 'Payouts and Taxes',
        icon: 'euro_symbol',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_PAT],
        items: [
          {
            title: 'Tax Traits',
            icon: 'analytics',
            route: '/pat/tax-traits',
          },
          {
            title: 'Securities',
            icon: 'account_balance',
            route: '/pat/securities',
          },
          {
            title: 'Payouts',
            icon: 'euro_symbol',
            route: '/pat/payouts',
          },
          {
            title: 'Paybacks',
            icon: 'currency_exchange',
            route: '/pat/paybacks',
          },
          {
            title: 'Management',
            icon: 'settings',
            route: '/pat/management',
          },
          {
            title: 'Reporting',
            icon: 'leaderboard',
            route: '/pat/reporting',
          },
          {
            title: 'History',
            icon: 'insights',
            route: '/pat/history',
          },
        ],
      },
      {
        title: 'EIE',
        icon: 'important_devices',
        roleGroups: [ROLE_GROUPS.SUPER_ADMIN, ROLE_GROUPS.ADMIN_EIE],
        items: [
          {
            title: 'Investments',
            icon: 'local_atm',
            route: '/eie/investments',
          },
          {
            title: 'Assets',
            icon: 'home_work',
            route: '/eie/assets',
          },
        ],
      },

    ].filter(menu => (
      !isProduction || menu.roleGroups.find(group => groups.includes(group))
    ));

    return {
      items: [
        { title: 'Home', icon: 'home', route: '/' },
        ...menuItems,
      ],
      right: null,
      drawer: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.userStore.user;
    },
  },
  methods: {
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    handleMenuItemClick(item) {
      if (item.route) {
        this.$router.push(item.route);
      }
    },
    isCurrentRoute(item) {
      if (item.items) {
        return this.isNestedRoute(item.items);
      }

      return this.$route.path === item.route;
    },
    isNestedRoute(items) {
      let result = false;
      items.map((item) => {
        if (this.$route.path === item.route) {
          result = true;
        }

        return item;
      });

      return result;
    },
    profile() {
      this.$router.push('/profile');
    },
    logOut() {
      signOut();
    },
  },
};
</script>
<style lang="scss" >
.progress {
  position: absolute;
  z-index: 5;
  display: block;
  width: 100%;
  margin-top: 88px;
}
.progressContent {
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
}
aside .v-list__group__header,
aside .v-list__tile,
aside .v-list__group__items > div {
  height: 48px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/shared/Menu.vue